import React from 'react';
import { useTranslation } from 'react-i18next';
import { autoRefreshTimeOptions } from '../../../../utils/local-storage/auto-refresh-monitoreos';
import {
  useDynamicViewState,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';
import { FormControl, Select, MenuItem, InputLabel } from '@material-ui/core';

/**
 * @fileoverview Componente para controlar el refresco automático del dashboard.
 * @module AutoRefresh
 */

/**
 * Componente AutoRefresh.
 *
 * Muestra un selector desplegable que permite al usuario configurar el intervalo
 * de actualización automática de los datos en el panel dinámico.
 *
 * Funcionalidad:
 * - Permite seleccionar intervalos predefinidos desde `autoRefreshTimeOptions`.
 * - Actualiza el estado global `refreshTime` en milisegundos.
 * - Este valor de tiempo se utiliza para configurar la propiedad `refetchInterval`
 *   de TanStack Query en las llamadas a la API de los componentes, permitiendo
 *   así el refresco automático de los datos sin recargar la página.
 *
 * @returns {React.ReactElement} Selector de tiempo de refresco.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function AutoRefresh() {
  const { t } = useTranslation();
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();

  /**
   * Maneja el cambio en el selector de tiempo de refresco.
   *
   * Despacha una acción para actualizar el tiempo de refresco en el contexto global.
   *
   * @param {Object} event - Evento de cambio del componente Select.
   * @param {number} event.target.value - El nuevo valor de tiempo en milisegundos.
   */
  function handleChangeAutoRefresh(event) {
    dispatch({
      type: 'SET_REFRESH_TIME',
      payload: event.target.value,
    });
  }

  return (
    <>
      <FormControl size="small" variant="outlined" fullWidth>
        <InputLabel id="select-refresh-label">
          {t('autoRefresh.autoRefresh')}
        </InputLabel>
        <Select
          labelId="select-refresh-label"
          id="select-time-refresh"
          value={state.refreshTime}
          label={t('autoRefresh.autoRefresh')}
          variant="outlined"
          size="small"
          onChange={handleChangeAutoRefresh}
          style={{ display: 'flex', backgroundColor: 'white' }}
        >
          <MenuItem value={0}>{t('autoRefresh.disable')}</MenuItem>
          {autoRefreshTimeOptions.map(({ label, value }) => {
            return (
              <MenuItem key={label} value={value}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </>
  );
}
